// src/utils/url.js
// Configuración centralizada de URLs y manejo de rutas absolutas

// 📍 Configuración base de la API - Única fuente de verdad
// 🔥 EXPORTAR API_BASE_URL como named export
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

// 📁 Mapeo completo de endpoints de la aplicación
export const API_ENDPOINTS = {
	// 📊 MÉTRICAS
	METRICAS_EMPRESAS: `${API_BASE_URL}/api/minutes/v1/metricas/empresas/`,
	METRICAS_EMPRESAS_DETALLADAS: `${API_BASE_URL}/api/minutes/v1/metricas/empresas/detalladas/`,
	METRICAS_CONTADORES: `${API_BASE_URL}/api/minutes/v1/metricas/contadores/`,
	METRICAS_CONTADORES_DETALLADAS: `${API_BASE_URL}/api/minutes/v1/metricas/contadores/detalladas/`,
  
	// 📤 EXPORTACIONES EXCEL DESDE DJANGO (URLs RELATIVAS)
	EXPORT_METRICAS_EMPRESAS_EXCEL: '/export/metricas/empresa/excel/',  // 🔥 Relativa
	EXPORT_METRICAS_CONTADORES_EXCEL: '/export/metricas/contadores/excel/',  // 🔥 Relativa
	EXPORT_METRICAS_COMPLETO_EXCEL: '/export/metricas/completo/excel/',  // 🔥 Relativa

	// 🏢 EMPRESAS - CORREGIDO
	EMPRESAS_LIST: `${API_BASE_URL}/api/company/v1/empresas/`,  // 🔥 Cambiado
	EMPRESAS_DETAIL: (nit) => `${API_BASE_URL}/api/company/v1/empresas/${nit}/`,
  
	// 👤 USUARIOS Y PERFILES
	// USUARIOS_LIST: `${API_BASE_URL}/api/user/v1/`,
	// USUARIOS_DETAIL: (id) => `${API_BASE_URL}/api/user/v1/${id}/`,
	// PERFILES_LIST: `${API_BASE_URL}/api/profiles/v1/`,
	// PERFIL_ACTUAL: `${API_BASE_URL}/api/profiles/v1/me/`,

	// 👤 USUARIOS Y PERFILES - CORREGIDO
	USUARIOS_LIST: `${API_BASE_URL}/api/user/v1/`,
	USUARIOS_LIST_USERS: `${API_BASE_URL}/api/user/v1/users/`,  // 🔥 Nuevo endpoint
	USUARIOS_DETAIL: (id) => `${API_BASE_URL}/api/user/v1/users/${id}/`,
	PERFILES_LIST: `${API_BASE_URL}/api/user/v1/profiles/`,
	PERFIL_ACTUAL: `${API_BASE_URL}/api/user/v1/my-profile/`,
  
	// 📝 ACTAS
	ACTAS_LIST: `${API_BASE_URL}/api/minutes/v1/actas/`,
	ACTAS_DETAIL: (id) => `${API_BASE_URL}/api/minutes/v1/actas/${id}/`,
  
	// 🔐 AUTENTICACIÓN
	AUTH_LOGIN: `${API_BASE_URL}/auth/jwt/create/`,
	AUTH_REFRESH: `${API_BASE_URL}/auth/jwt/refresh/`,
	AUTH_VERIFY: `${API_BASE_URL}/auth/jwt/verify/`,
	AUTH_REGISTER: `${API_BASE_URL}/auth/users/`,
	AUTH_ACTIVATION: `${API_BASE_URL}/auth/users/activation/`,
  
	// 📊 DASHBOARD (si existe)
	DASHBOARD_STATS: `${API_BASE_URL}/api/dashboard/v1/stats/`,
};

// 🔗 Función para obtener URL absoluta
export const getAbsoluteUrl = (relativeUrl) => {
  	if (!relativeUrl) return null;
  
	// Si ya es una URL absoluta, retornarla directamente
	if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
		return relativeUrl;
	}
  
	// Si empieza con /, concatenar con base URL
	if (relativeUrl.startsWith('/')) {
		return `${API_BASE_URL}${relativeUrl}`;
	}
  
	// Si no empieza con /, asumir que ya incluye el prefijo de API
	// o necesita el base URL completo
	return `${API_BASE_URL}/${relativeUrl}`;
};

// 🖼️ Función para obtener URL de imagen
export const getImageUrl = (imagePath) => {
  	if (!imagePath) return null;
  
	// Si ya es una URL completa o empieza con data: (base64)
	if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
		return imagePath;
	}
  
	// Si es una ruta relativa a assets del frontend
	if (imagePath.startsWith('/') || imagePath.startsWith('./') || imagePath.startsWith('../')) {
		return imagePath;
	}
	
	// Asumir que es una ruta del servidor Django
	return getAbsoluteUrl(imagePath);
};

// 🛠️ Función para manejar errores de carga de imágenes
export const handleImageError = (event, options = {}) => {
	const {
		fallbackText = '',
		showPlaceholder = true,
		placeholderClass = 'bg-gray-200 flex items-center justify-center text-gray-500'
	} = options;
  
  	const imgElement = event.target;
  
  	if (showPlaceholder) {
		// Reemplazar la imagen por un div placeholder
		const parent = imgElement.parentElement;
		const placeholder = document.createElement('div');
    
		placeholder.className = `${placeholderClass} ${imgElement.className}`;
		placeholder.style.width = imgElement.style.width || getComputedStyle(imgElement).width;
		placeholder.style.height = imgElement.style.height || getComputedStyle(imgElement).height;
    
		if (fallbackText) {
		placeholder.textContent = fallbackText.substring(0, 2).toUpperCase();
		} else if (imgElement.alt) {
		placeholder.textContent = imgElement.alt.substring(0, 2).toUpperCase();
		} else {
		placeholder.textContent = '??';
		}
    
    	parent.replaceChild(placeholder, imgElement);
  	} else {
    	// Simplemente ocultar la imagen
    	imgElement.style.display = 'none';
  	}
  
	// Registrar el error en consola para depuración
	//console.warn('🖼️ Error cargando imagen:', {
	//	src: imgElement.src,
	//	alt: imgElement.alt,
	//	resolvedSrc: getAbsoluteUrl(imgElement.src)
	//});
};

// Función auxiliar para construir URLs con parámetros
export const buildUrlWithFilters = (baseUrl, filters = {}) => {
  	const params = new URLSearchParams();
  
	Object.entries(filters).forEach(([key, value]) => {
		if (value && String(value).trim() !== '') {
			params.append(key, value);
		}
	});
  
  	const queryString = params.toString();
  	return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

// 🎯 Helper específico para exportaciones Excel
export const getExportUrl = (type, filters = {}) => {
  	const API_PREFIX = '/api/minutes/v1';
  	const basePath = `${API_PREFIX}/export/metricas/`;
  
	switch(type) {
		case 'empresas':
			const empresaNombre = filters.empresa_nombre || '';
			const  filtersWithoutEmpresa = { ...filters };
			delete filtersWithoutEmpresa.empresa_nombre;
			const encodedNombre = encodeURIComponent(empresaNombre);
			//console.log('desde utisl url ',encodedNombre)			
			return buildUrlWithFilters(
				`${basePath}empresa/${encodedNombre}/excel/`, 
				filtersWithoutEmpresa
			);
		case 'contadores':
			return buildUrlWithFilters(`${basePath}contadores/excel/`, filters);
		case 'completo':
			return buildUrlWithFilters(`${basePath}completo/excel/`, filters);
		case 'profesional':
		// ❌ Esto no funciona porque necesita username
			//console.warn('⚠️ getExportUrl con tipo "profesional" requiere username. Usa getExportProfesionalUrl()');
			return null;
		default:
			throw new Error(`Tipo de exportación no soportado: ${type}`);
	}
};

export const getExportProfesionalUrl = (profesionalUsername, filters = {}) => {
	const API_PREFIX = '/api/minutes/v1';
	const baseUrl = `${API_PREFIX}/export/metricas/profesional/${profesionalUsername}/excel/`;
	return buildUrlWithFilters(baseUrl, filters);
};


// 🔄 Helper para paginación
export const buildPaginationUrl = (baseUrl, page = 1, pageSize = 10, filters = {}) => {
	const paginationFilters = {
		...filters,
		page,
		page_size: pageSize
	};
	
	return buildUrlWithFilters(baseUrl, paginationFilters);
};

// 🏷️ Helper para obtener nombre de archivo de URL
export const getFilenameFromUrl = (url) => {
 	if (!url) return 'descarga';
  
  	try {
		// Intentar extraer nombre de Content-Disposition si está disponible
		// (para respuestas de servidor)
		if (url instanceof Response) {
			const disposition = url.headers.get('content-disposition');
			if (disposition) {
				const match = disposition.match(/filename="?([^"]+)"?/);
				if (match && match[1]) {
					return match[1];
				}
			}
		}
    
		// Extraer de la URL
		const urlObj = new URL(getAbsoluteUrl(url));
		const pathname = urlObj.pathname;
		const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
		
		// Si no hay nombre de archivo, generar uno basado en fecha
		if (!filename || filename === pathname) {
			return `export-${new Date().toISOString().split('T')[0]}`;
		}
    
    	return filename;
  	} catch (error) {
    	//console.warn('⚠️ Error obteniendo nombre de archivo:', error);
    	return `export-${new Date().toISOString().split('T')[0]}`;
  	}
};

// 🎛️ Configuración global de la aplicación
export const APP_CONFIG = {
	// 🔥 NOTA: Usamos API_BASE_URL directamente, no lo duplicamos
	APP_NAME: import.meta.env.VITE_APP_NAME || 'Sistema de Gestión',
	APP_VERSION: import.meta.env.VITE_APP_VERSION || '1.0.0',
	ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT || 'development',
	
	// Configuración de paginación por defecto
	DEFAULT_PAGE_SIZE: 10,
	DEFAULT_PAGE_SIZES: [5, 10, 25, 50, 100],
  
	// Configuración de exportaciones
	EXPORT_FILE_TYPES: {
		EXCEL: 'excel',
		PDF: 'pdf',
		CSV: 'csv'
	},
  
	// Timeouts en milisegundos
	API_TIMEOUT: 30000, // 30 segundos
	UPLOAD_TIMEOUT: 60000, // 1 minuto para uploads
	EXPORT_TIMEOUT: 120000, // 2 minutos para exportaciones grandes
};

// 📦 Exportar también como default para compatibilidad
export default {
	API_BASE_URL,
	API_ENDPOINTS,
	getAbsoluteUrl,
	getImageUrl,
	handleImageError,
	buildUrlWithFilters,
	getExportUrl,
	buildPaginationUrl,
	getFilenameFromUrl,
	APP_CONFIG
};
